import React, { useEffect, useState } from 'react';
import { FlatList, Image, ActivityIndicator } from 'react-native';
import { Switch, Toggle } from '../../Components';
import SearchBox from '../../Components/SearchBox';
import type { AthleteProps, EventProps, MatchProps, TournamentProps, TradeProps } from '../../types';
import { ManageCompetitionMatchApi } from '../api';
import { Button, Text, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';

type AthleteSelectorProps = {
    event?:EventProps,
    tournament?:TournamentProps,
    match?:MatchProps,
    selected_athletes: string[],
    trades:TradeProps[],
    filtered_athletes:string[],
    filtered_positions:string[],
    active_market?:string,
    onSelectAthlete:(athlete:AthleteProps) => void,
    onDeselectAthlete:(athlete:AthleteProps) => void,
    onClose:() => void
}

const AthleteSelector = ({ event, tournament, match, trades, active_market, filtered_athletes, selected_athletes, filtered_positions, onSelectAthlete, onDeselectAthlete, onClose }:AthleteSelectorProps) => {
    const Colors = useColors();
    const [ search_value, setSearchValue ] = useState('');
    const [ selector_data, setSelectorData ] = useState<{
        loading:boolean,
        athletes:AthleteProps[],
        trade_athletes:AthleteProps[],
        event_side:string
    }>({
        loading: false,
        athletes: [],
        trade_athletes: [],
        event_side: 'away'
    })
    const { loading, event_side, trade_athletes } = selector_data;
    
    let available_athletes = trade_athletes.filter(a => `${a.first_name} ${a.last_name}`.toLowerCase().includes(search_value.toLowerCase()));
    if(filtered_athletes.length > 0){
        available_athletes = available_athletes.filter(a => filtered_athletes.map(a => a.toString()).includes(a.athlete_id.toString()))
    }
    if(available_athletes.length > 0){
        available_athletes = available_athletes.filter(a => filtered_positions.includes(a.position))
    }
    if(event && event_side){
        available_athletes = available_athletes.filter(a => a.team_id == event[event_side as keyof EventProps]?.team_id)
    }

    useEffect(() => {
        getAthletesFromServer();
    },[event, tournament, match])

    const getAthletesFromServer = async() => {
        setSelectorData({ ...selector_data, loading:true });
        let avail_athletes:AthleteProps[] = []
        if(event){
            let away_athletes = await ManageCompetitionMatchApi.getAthletesByTeamId(event.away_team_id);
            let home_athletes = await ManageCompetitionMatchApi.getAthletesByTeamId(event.home_team_id);
            avail_athletes = away_athletes.concat(home_athletes);
        }
        //Get all the side ids for this market
        let trade_athlete_ids = [ ...new Set(trades.filter(t => t.event_type == 'team' && t.event_id == event?.event_id && t.market_id == active_market && t.side_type == 'athlete').map(t => t.side_id)) ]
        let trade_athletes_avail = avail_athletes.filter(a => trade_athlete_ids.includes(a.athlete_id))
        //Lets filter
        setSelectorData({
            ...selector_data,
            loading:false,
            trade_athletes: trade_athletes_avail,
            athletes: avail_athletes
        })
    }

    const renderAthletes = (data: { item:AthleteProps, index:number }) => {
        const selected = selected_athletes.includes(data.item.athlete_id) ? true : false
        return (
            <Button style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }} onPress={() => selected ? onDeselectAthlete(data.item) : onSelectAthlete(data.item)}>
                <Image
                    source={{ uri: data.item.image?.url ?? data.item.team?.image?.url ?? '' }}
                    style={{ height:30, width:30 }}
                    resizeMode='cover'
                />                
                <View transparent style={{ flex:1, marginLeft:10, marginRight:10 }}>
                    <Text size={14} theme='h1'>{data.item.first_name} {data.item.last_name} ({data.item.position})</Text>
                    <Text style={{ marginTop:3 }} size={14} theme='description'>{data.item.team?.market_name??''} {data.item.team?.name ?? ''}</Text>
                </View>
                <Switch 
                    switch_type="on_off"
                    value={selected}
                    onChange={() => {
                        if(!selected){ return onSelectAthlete(data.item) }
                        return onDeselectAthlete(data.item)
                    }}
                />
            </Button>
        )
    }

    return (
        <View style={{ flex:1 }}>
            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:20 }}>
                <View transparent style={{ flex:1 }}>
                    <Text theme='h1'>MANAGE ATHLETES</Text>
                </View>
            </View>
            <View style={{ flex:1 }}>
                <View type='body'>
                {event?.away && event?.home ?
                <View style={{ padding:10 }}>
                     <Toggle
                        options={[
                            {key: 'away', label: event.away.abbr},
                            {key:'home', label: event.home.abbr}
                        ]}
                        selected_option={event_side}
                        onSelectOption={(option) => setSelectorData({ ...selector_data, event_side: option })}
                    />
                </View>
                   
                :<></>}
                    <View style={{ marginTop:0 }}>
                        <SearchBox 
                            onChange={(text) => setSearchValue(text)}
                            hide_search_button
                        />
                    </View>
                    {loading ?
                    <ActivityIndicator size='large' color={Colors.text.h1} style={{ padding:20, alignSelf:'center' }} />
                    :<></>}
                    <FlatList
                        data={available_athletes}
                        renderItem={renderAthletes}
                        keyExtractor={(item) => item.athlete_id.toString()}
                    />
                </View>
            </View>
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                <Button
                    title='CLOSE'
                    type='error'
                    style={{flex:1}}
                    onPress={() => onClose()}
                />
            </View>
        </View>
    )
}

export default AthleteSelector